import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CaretDown, CheckCircle, Circle } from "@phosphor-icons/react";
import {
  useReceptionStatuses,
  iconForStatus,
  withAlpha,
} from "@/lib/receptionStatuses";

const MENU_W = 190;
const MENU_H_APPROX = 44;

/** Lookup a status object from the loaded catalog by key. */
export function findReceptionStatus(list, key) {
  if (!list || list.length === 0) return null;
  const k = (key || "").toLowerCase();
  return list.find((s) => s.key === k) || list.find((s) => s.is_default) || list[0];
}

export function ReceptionChip({ status, onChange, disabled, testId }) {
  const { list } = useReceptionStatuses();
  const meta = findReceptionStatus(list, status);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const computePosition = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuH = MENU_H_APPROX + Math.min(list.length, 8) * 30;
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < menuH + 8 ? r.top - menuH - 4 : r.bottom + 4;
    const left = Math.min(Math.max(8, r.right - MENU_W), window.innerWidth - MENU_W - 8);
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    computePosition();
    const onDoc = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, list.length]);

  if (!meta) {
    // catalog still loading
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm font-mono-data text-[10px] uppercase tracking-widest border border-white/10 text-white/40"
      >
        <Circle size={11} weight="fill" /> …
      </span>
    );
  }

  const Icon = iconForStatus(meta);
  const color = meta.color || "#A78BFA";
  const currentKey = meta.key;

  const pick = (next) => {
    setOpen(false);
    if (next === currentKey) return;
    onChange?.(next);
  };

  return (
    <>
      <button
        ref={btnRef}
        data-testid={testId}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm font-mono-data text-[10px] uppercase tracking-widest border transition-colors hover:brightness-125 disabled:opacity-60 max-w-[180px]"
        style={{ color, backgroundColor: withAlpha(color, 0.14), borderColor: `${color}55` }}
        title={meta.label}
      >
        <Icon size={11} weight="fill" />
        <span className="truncate">{meta.label}</span>
        {!disabled && <CaretDown size={9} className="opacity-60 shrink-0" />}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={testId ? `${testId}-menu` : undefined}
            style={{
              position: "fixed", top: pos.top, left: pos.left,
              width: MENU_W, zIndex: 9999, backgroundColor: "#111111",
            }}
            className="border border-white/15 rounded-sm shadow-2xl shadow-black/50 py-1"
          >
            {list.map((s) => {
              const KIcon = iconForStatus(s);
              const c = s.color || "#A78BFA";
              return (
                <button
                  key={s.key}
                  onClick={(e) => { e.stopPropagation(); pick(s.key); }}
                  data-testid={testId ? `${testId}-opt-${s.key}` : undefined}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/5 transition-colors text-left"
                  style={{ color: c }}
                >
                  <KIcon size={11} weight="fill" />
                  <span className="flex-1 truncate">{s.label}</span>
                  {s.key === currentKey && <CheckCircle size={11} weight="fill" className="opacity-60" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}
